/**
 * §P.1 · Alumnos y familias.
 *
 * Las formas que devuelve la API, en lo que usa la pantalla. Eden ya trae los
 * tipos del servidor; esto es lo que la pantalla necesita nombrar para pintar, y
 * separarlo evita que cada componente afirme su propia versión.
 */

/**
 * Los cuatro estados, que son los del `CHECK` de la columna.
 *
 * `trial` se me había pasado al escribir el filtro y lo cazó el compilador: es
 * exactamente para lo que está Eden. Un alumno de prueba que no sale en ningún
 * filtro es un alumno que nadie da de alta ni de baja.
 */
export type EstadoAlumno = 'active' | 'trial' | 'paused' | 'left';

/**
 * Un alumno.
 *
 * **Las fechas son `Date`, no texto**: Eden deserializa todo lo que el esquema del
 * servidor marca como `format: 'date'` o `'date-time'`. Pintarlas directamente en
 * un JSX deja la pantalla en blanco; van por `fecha()` de `shared/ui/formato`.
 */
export interface Alumno {
  readonly id: string;
  readonly code: string | null;
  readonly first_name: string;
  readonly last_name: string | null;
  readonly birthdate: Date | null;
  readonly age: number | null;
  readonly status: string;
  readonly joined_on: Date;
  readonly left_on: Date | null;
  readonly current_rank_id: string | null;
  readonly current_stripes: number;
}

export interface PaginaDeAlumnos {
  readonly data: readonly Alumno[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

export interface Grupo {
  readonly id: string;
  readonly name: string;
  readonly venue_name: string | null;
  readonly enrolled_count: number;
  readonly active: boolean;
}

/**
 * Un tutor de un alumno.
 *
 * `membership_status` es el campo que `BFF_BO.md` §7.1 manda enseñar: `invited`
 * significa que se mandó el correo y esa persona **todavía no ha entrado**;
 * `active`, que ya está dentro. «Un club que no sabe quién ha aceptado acaba
 * llamando por teléfono a las 120 familias.»
 */
export interface Tutor {
  readonly user_id: string;
  readonly email: string | null;
  readonly first_name: string | null;
  readonly last_name: string | null;
  readonly relationship: string | null;
  readonly is_primary: boolean;
  readonly can_pickup: boolean;
  readonly receives_billing: boolean;
  readonly access_level: string;
  readonly membership_status: string;
}

export const nombreCompleto = (a: {
  first_name: string;
  last_name: string | null;
}): string => [a.first_name, a.last_name].filter(Boolean).join(' ');

export const nombreDeTutor = (t: Tutor): string =>
  [t.first_name, t.last_name].filter(Boolean).join(' ') ||
  t.email ||
  'Sin nombre';
